import Button from '@/common/components/Button';
import { FeeTextField } from '@/common/components/FeeTextField';
import SelectController from '@/common/components/SelectController';
import useDebounce from '@/common/hooks/useDebounce';
import { Mapper } from '@/common/services/mapper';
import { unFormatCurrency, unFormatPercentage } from '@/common/utils/unFormatters';
import { useEstablishmentCompanies } from '@/features/establishments/hooks/useEstablishmentCompanies';
import { EstablishmentCompanyTransformer } from '@/features/establishments/services/establishmentCompanyTransformer';
import { useFeeTemplates } from '@/features/feeTemplates/hooks/useFeeTemplates';
import { FeeTemplateData } from '@/features/feeTemplates/types/feeTemplates.type';
import { SpecialEntriesData } from '@/features/privateCompanyLeadsFunnel/types/privateCompanyLead';
import { UseMutateFunction, useQueryClient } from '@tanstack/react-query';
import { XIcon } from 'lucide-react';
import { Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';
import { PrivateCompanyLeadsFunnelUpdateValidationSchema } from '../schemas/update.schema';
import usePrivateCompanyLead from '../hooks/usePrivateCompanyLead';
import { useUpdatePrivateCompanyLeadSpecialFee } from '../hooks/useUpdatePrivateCompanyLeadSpecialFee';
import { Control, FieldErrors } from 'react-hook-form';

interface SpecialFeeItemProps {
  control: Control<PrivateCompanyLeadsFunnelUpdateValidationSchema>;
  errors: FieldErrors<PrivateCompanyLeadsFunnelUpdateValidationSchema>;
  specialEntry: SpecialEntriesData;
  setSpecialEntries: Dispatch<SetStateAction<Partial<SpecialEntriesData>[]>>;
  index: number;
  createPrivateCompanyLeadSpecialFee?: UseMutateFunction<
    void,
    Error,
    { value: string; type: string; fee_template_id: string; },
    unknown
  >;
  createPrivateCompanyLeadSpecialFeeIsPending?: boolean;
  deleteSpecialEntry: (props: { id: string; temporary?: boolean; }) => void;
  disabled?: boolean;
}

export const SpecialFeeItem = ({
  control,
  errors,
  specialEntry,
  setSpecialEntries,
  index,
  createPrivateCompanyLeadSpecialFee,
  createPrivateCompanyLeadSpecialFeeIsPending,
  deleteSpecialEntry,
  disabled,
}: SpecialFeeItemProps) => {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const [searchEstablishment, setSearchEstablishment] = useDebounce('');
  const [searchFeeTemplate, setSearchFeeTemplate] = useDebounce('');

  const { isFetching: privateCompanyIsFetching } = usePrivateCompanyLead(id || '', false);

  const { data: establishmentOptions } = useEstablishmentCompanies({
    filters: { search: searchEstablishment },
    mapper: Mapper.mapWithTransformer({
      transformer: EstablishmentCompanyTransformer.optionTransformer({ valueFieldName: 'id', addAllEstablishmentOption: true }),
    }),
  });

  const { data: feeTemplateOptions } = useFeeTemplates({
    filters: { search: searchFeeTemplate, category: 'establishment' },
    mapper: Mapper.mapToOptions<FeeTemplateData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  });

  const { mutate: updatePrivateCompanyLeadSpecialFee, isPending: updateSpecialFeeIsPending } =
    useUpdatePrivateCompanyLeadSpecialFee({
      privateCompanyLeadId: id,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', id] });
      },
    });

  const submitNewValue = ({ id, value, calculate_as }: { id: string; value: string; calculate_as: string; }) => {
    if (!/R\$|%/.test(value)) {
      return;
    }

    const unFormatFunction = calculate_as === 'percentage' ? unFormatPercentage : unFormatCurrency;
    value = value && `${unFormatFunction(value)}`;

    updatePrivateCompanyLeadSpecialFee({ id, value });
  };

  return (
    <div key={specialEntry.id} className="space-y-4">
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline-error"
          className="size-[54px] mt-auto"
          onClick={() => deleteSpecialEntry({ id: specialEntry.id, temporary: !specialEntry.fee_template })}
          disabled={updateSpecialFeeIsPending || privateCompanyIsFetching || disabled}
        >
          <XIcon />
        </Button>

        <SelectController
          control={control}
          name={`fee_rule.special_entries.${index}.target`}
          label="Estabelecimento"
          className="w-full"
          onInputChange={setSearchEstablishment}
          value={
            specialEntry.temporary_target_id
              ? { label: specialEntry.temporary_target_name, value: specialEntry.temporary_target_id }
              : specialEntry.target
              ? EstablishmentCompanyTransformer.optionTransformer()([specialEntry.target])[0]
              : {
                  label: 'Todos os estabelecimentos',
                  value: 'all',
                  type: 'Establishment::Company',
                }
          }
          triggerOnChange={(newValue: { label: string; value: string; type: string; }) => {
            specialEntry.target === undefined
              ? setSpecialEntries((prev) => {
                const newSpecialEntries = [...prev];
                newSpecialEntries[index] = {
                  id: newValue.value,
                  temporary_target_id: newValue.value,
                  temporary_target_name: newValue.label,
                  temporary_target_type: newValue.type,
                };
                return newSpecialEntries;
              })
              : updatePrivateCompanyLeadSpecialFee({
                id: specialEntry.id,
                target_id: newValue.value,
                target_type: newValue.type,
              });
          }}
          options={establishmentOptions || []}
          isDisabled={updateSpecialFeeIsPending || privateCompanyIsFetching || disabled}
          isLoading={createPrivateCompanyLeadSpecialFeeIsPending}
        />
      </div>

      <div className="flex gap-2">
        <SelectController
          control={control}
          label="Tarifa"
          className="w-full"
          name={`fee_rule.special_entries.${index}.fee_template_id`}
          value={{ label: specialEntry.fee_template?.display_name, value: specialEntry.fee_template?.id }}
          onInputChange={setSearchFeeTemplate}
          triggerOnChange={(newValue: { label: string; value: string; }) => {
            specialEntry.temporary_target_id &&
              specialEntry.temporary_target_type &&
              createPrivateCompanyLeadSpecialFee?.({
                value: specialEntry.temporary_target_id,
                type: specialEntry.temporary_target_type,
                fee_template_id: newValue.value,
              });
          }}
          options={feeTemplateOptions || []}
          isDisabled={!specialEntry.temporary_target_id || updateSpecialFeeIsPending || privateCompanyIsFetching || disabled}
          isLoading={createPrivateCompanyLeadSpecialFeeIsPending}
        />

        {specialEntry.fee_template && (
          <FeeTextField
            contractFee={specialEntry}
            fieldName={`fee_rule.special_entries.${index}.value`}
            submit={submitNewValue}
            disabled={updateSpecialFeeIsPending || privateCompanyIsFetching || disabled}
            errorMessage={errors?.fee_rule?.special_entries?.[index]?.value?.message}
            className="w-full"
          />
        )}
      </div>
    </div>
  );
};
